/**
 * Tauri File Drop Hook
 *
 * Handles file drop events specific to the Tauri desktop app.
 * Falls back gracefully when not running in Tauri.
 */

import { useEffect, useCallback } from 'react';
import { toast } from 'sonner';

// Type declarations for Tauri API when running in desktop
declare global {
  interface Window {
    __TAURI__?: {
      fs: {
        readTextFile: (path: string) => Promise<string>;
      };
      path: {
        basename: (path: string) => Promise<string>;
      };
      event: {
        listen: (
          event: string,
          handler: (event: any) => void
        ) => Promise<() => void>;
      };
    };
  }
}

// interface TauriFile {
//   path: string;
//   name: string;
//   size: number;
// }

interface TauriDropEvent {
  payload: {
    paths: string[];
    position: { x: number; y: number };
  };
}

export function useTauriFileDrop(
  onFileDrop?: (files: File[]) => void,
  enabled = true
) {
  const isTauri = typeof window !== 'undefined' && '__TAURI__' in window;

  const handleTauriDrop = useCallback(
    async (event: TauriDropEvent) => {
      if (!enabled || !onFileDrop) return;

      try {
        // In Tauri, we get file paths, not File objects
        // We need to read the files using Tauri's fs API
        const { paths } = event.payload;

        if (paths.length === 0) return;

        // Use Tauri APIs directly from window object
        if (!window.__TAURI__) {
          throw new Error('Tauri API not available');
        }

        const { readTextFile } = window.__TAURI__.fs;
        const { basename } = window.__TAURI__.path;

        const files: File[] = [];

        for (const path of paths) {
          try {
            // Get file name from path
            const fileName = await basename(path);

            // Check if it's a supported file type
            const extension = '.' + fileName.split('.').pop()?.toLowerCase();
            const supportedExtensions = [
              '.md',
              '.txt',
              '.json',
              '.mdx',
              '.phrasewright',
            ];

            if (!supportedExtensions.includes(extension)) {
              toast.error(`Unsupported file type: ${fileName}`);
              continue;
            }

            // Read file content
            const content = await readTextFile(path);

            // Create a File object from the content
            const file = new File([content], fileName, {
              type: getMimeType(extension),
            });

            files.push(file);
          } catch (error) {
            console.error(`Failed to read file: ${path}`, error);
            toast.error(`Failed to read file: ${path}`);
          }
        }

        if (files.length > 0) {
          onFileDrop(files);
        }
      } catch (error) {
        console.error('Failed to handle Tauri file drop:', error);
        toast.error('Failed to import dropped files');
      }
    },
    [enabled, onFileDrop]
  );

  useEffect(() => {
    if (!isTauri || !enabled) return;

    // Set up Tauri file drop listener
    let unlisten: (() => void) | undefined;

    const setupListener = async () => {
      try {
        if (!window.__TAURI__) return;

        const { listen } = window.__TAURI__.event;

        // Listen for file drop events
        unlisten = await listen('tauri://file-drop', handleTauriDrop);

        // Also listen for file drop hover events for visual feedback
        const unlistenHover = await listen(
          'tauri://file-drop-hover',
          (event) => {
            // Could dispatch a custom event here for visual feedback
            window.dispatchEvent(
              new CustomEvent('tauri-file-hover', {
                detail: event.payload,
              })
            );
          }
        );

        // Clean up both listeners
        const originalUnlisten = unlisten;
        unlisten = () => {
          originalUnlisten();
          unlistenHover();
        };
      } catch (error) {
        console.error('Failed to set up Tauri file drop listener:', error);
      }
    };

    setupListener();

    return () => {
      unlisten?.();
    };
  }, [isTauri, enabled, handleTauriDrop]);

  return { isTauri };
}

// Helper function to get MIME type from extension
function getMimeType(extension: string): string {
  const mimeTypes: Record<string, string> = {
    '.md': 'text/markdown',
    '.mdx': 'text/mdx',
    '.txt': 'text/plain',
    '.json': 'application/json',
    '.phrasewright': 'application/json',
  };

  return mimeTypes[extension] || 'text/plain';
}
